import React, { useEffect, useState } from "react";
import { Create, SimpleForm, AutocompleteInput } from "react-admin";
import {
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import { VehicleFields } from "./VehicleFields";
import { useWatch, useFormContext } from "react-hook-form";
import VehicleBulkUpload from "./VehicleBulkUpload";
import { calculateRemainingFinance, cleanDeferredInstallments } from "./vehicleFinance";


export const VehicleCreate = () => {
  const [companies, setCompanies] = useState([]);

  // 🔹 Fetch company names dynamically
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setCompanies(list);
    });
    return () => unsub();
  }, []);

  // 🔹 Internal helper to upload a single file field
  const uploadFile = async (fileObj, folder, vehicleNumber) => {
    if (!fileObj || !fileObj.rawFile) return null;
    const storageRef = ref(
      storage,
      `${folder}/${vehicleNumber || "vehicle"}_${Date.now()}_${
        fileObj.name
      }`
    );
    await uploadBytes(storageRef, fileObj.rawFile);
    return await getDownloadURL(storageRef);
  };

  // 🔹 Save new vehicle with file uploads
  const handleSave = async (values) => {
    try {
      const companyName =
        companies.find((c) => c.name === values.company)?.name ||
        values.company ||
        "";

      // ✅ Add new company if it doesn't exist yet
      if (companyName && !companies.some((c) => c.name === companyName)) {
        await addDoc(collection(db, "companies"), { name: companyName });
      }

      // ✅ Upload docs
      const uploaded = {
        insuranceFile: await uploadFile(
          values.insuranceFile,
          "vehicles/insurance",
          values.vehicleNumber
        ),
        registrationFile: await uploadFile(
          values.registrationFile,
          "vehicles/registration",
          values.vehicleNumber
        ),
        financeFile: await uploadFile(
          values.financeFile,
          "vehicles/finance",
          values.vehicleNumber
        ),
      };

      const deferredInstallments = cleanDeferredInstallments(
        values.deferredInstallments,
        values.monthlyInstallment
      );

      // ✅ Calculate remaining finance one more time at submit
      const remainingFinance = calculateRemainingFinance({
        ...values,
        deferredInstallments,
      });

      // ✅ Write document
      await addDoc(collection(db, "vehicles"), {
        vehicleNumber: values.vehicleNumber || "",
        vehicleType: values.vehicleType || "",
        vehicleYear: values.vehicleYear || "",
        company: companyName,

        insuranceExpiry: values.insuranceExpiry || null,
        registrationExpiry: values.registrationExpiry || null,

        mortgageFrom: values.mortgageFrom || "",
        installmentBank: values.installmentBank || "",
        totalFacilityAmount: toNumber(values.totalFacilityAmount),
        monthlyInstallment: toNumber(values.monthlyInstallment),
        installmentStart: values.installmentStart || null,
        installmentEnd: values.installmentEnd || null,

        // finance adjustment / deferment
        deferredInstallments,
        manualRevisedEndDate: values.manualRevisedEndDate || null,
        bankCharges: toNumber(values.bankCharges) || 0,

        // final settlement
        settlementDate: values.settlementDate || null,
        settlementAmount: toNumber(values.settlementAmount) || 0,
        settlementNotes: values.settlementNotes || "",

        remainingFinance,

        insuranceFile: uploaded.insuranceFile,
        registrationFile: uploaded.registrationFile,
        financeFile: uploaded.financeFile,

        createdAt: serverTimestamp(),
        createdBy: localStorage.getItem("email") || "",
      });

      alert("✅ Vehicle created successfully!");
      window.location.hash = "#/vehicles";
    } catch (err) {
      console.error("❌ Error adding vehicle:", err);
      alert("❌ Error: " + err.message);
    }
  };

  const toNumber = (val) => (Number(val) ? Number(val) : null);

  // 🔹 This component lives INSIDE the form and keeps remainingFinance updated live
  const FormWatcher = () => {
    const { setValue } = useFormContext();

    const watched = useWatch({
      name: [
        "totalFacilityAmount",
        "monthlyInstallment",
        "installmentStart",
        "installmentEnd",
        "mortgageFrom",
        "deferredInstallments",
        "manualRevisedEndDate",
        "bankCharges",
        "settlementDate",
        "soldAt",
      ],
    });

    useEffect(() => {
      if (!watched) return;
      const rem = calculateRemainingFinance({
        totalFacilityAmount: watched[0],
        monthlyInstallment: watched[1],
        installmentStart: watched[2],
        installmentEnd: watched[3],
        mortgageFrom: watched[4],
        deferredInstallments: cleanDeferredInstallments(watched[5], watched[1]),
        manualRevisedEndDate: watched[6],
        bankCharges: watched[7],
        settlementDate: watched[8],
        soldAt: watched[9],
      });
      setValue("remainingFinance", rem); // <-- writes into the disabled field
    }, [watched, setValue]);

    return null;
  };

  return (
    <Create title="Add New Vehicle">
      <VehicleBulkUpload />

      <SimpleForm onSubmit={handleSave}>
        {/* Invisible watcher that drives remainingFinance */}
        <FormWatcher />

        <AutocompleteInput
          source="company"
          label="Company"
          choices={companies}
          optionText="name"
          optionValue="name"
          fullWidth
          helperText="Select company or type to create new"
          freeSolo
        />

        <VehicleFields />
      </SimpleForm>
    </Create>
  );
};
